import { useState } from "react";
import { ArrowUpRight, ChevronDown, ChevronUp, ChevronsUpDown, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import type { BiteLink } from "@/components/reports/BitePanel";
import { SECTION_TITLE } from "@/components/reports/report_rendering/config";
import type { AllBite } from "@/api/classroom-render";

export type { AllBite };

// ---------- ComboFilter ----------

function ComboFilter({
  label,
  options,
  value,
  onChange,
}: {
  label: string;
  options: string[];
  value: string;
  onChange: (v: string) => void;
}) {
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);

  const filtered = options.filter((o) =>
    o.toLowerCase().startsWith(search.toLowerCase()),
  );

  const handleSelect = (opt: string) => {
    onChange(opt);
    setSearch("");
    setOpen(false);
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange("");
    setSearch("");
  };

  return (
    <DropdownMenu
      open={open}
      onOpenChange={(o) => {
        setOpen(o);
        if (!o) setSearch("");
      }}
    >
      <DropdownMenuTrigger asChild>
        <button className="flex items-center gap-1 text-xs font-medium text-muted-foreground hover:text-foreground transition-colors">
          {label}
          {value ? (
            <span className="ml-1 flex items-center gap-0.5 rounded bg-primary/15 px-1 py-0.5 text-primary text-xs">
              {value}
              <X className="size-3 cursor-pointer" onClick={handleClear} />
            </span>
          ) : (
            <ChevronsUpDown className="size-3 opacity-50" />
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        className="w-56 p-1"
        onCloseAutoFocus={(e) => e.preventDefault()}
      >
        <div className="px-1 pb-1">
          <Input
            autoFocus
            placeholder={`Search ${label.toLowerCase()}…`}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="h-7 text-xs"
            onKeyDown={(e) => {
              if (e.key === "Enter" && filtered.length > 0)
                handleSelect(filtered[0]!);
              e.stopPropagation();
            }}
          />
        </div>
        <div className="max-h-48 overflow-y-auto">
          {filtered.length === 0 ? (
            <p className="px-2 py-1.5 text-xs text-muted-foreground">
              No matches
            </p>
          ) : (
            filtered.map((opt) => (
              <DropdownMenuItem
                key={opt}
                className="text-xs cursor-pointer"
                onSelect={() => handleSelect(opt)}
              >
                {opt}
              </DropdownMenuItem>
            ))
          )}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

// ---------- SortIcon ----------

type SortCol = "lesson" | "slide" | "student" | "score" | "time" | "qa_total" | "qa_incorrect";

function SortIcon({
  col,
  sortCol,
  sortDir,
}: {
  col: SortCol;
  sortCol: SortCol;
  sortDir: "asc" | "desc";
}) {
  if (col !== sortCol)
    return <ChevronsUpDown className="size-3 opacity-30 inline ml-0.5" />;
  return sortDir === "asc" ? (
    <ChevronUp className="size-3 inline ml-0.5" />
  ) : (
    <ChevronDown className="size-3 inline ml-0.5" />
  );
}

// ---------- AllBitesTable ----------

function fmtTime(ms: number | null): string {
  if (ms == null) return "—";
  const s = Math.round(ms / 1000);
  const m = Math.floor(s / 60);
  return m > 0 ? `${m}m ${s % 60}s` : `${s}s`;
}

export function AllBitesTable({
  allBites,
  onOpenBite,
  isLesson = false,
}: {
  allBites: AllBite[];
  onOpenBite: (link: BiteLink) => void;
  isLesson?: boolean;
}) {
  const [filterLesson, setFilterLesson] = useState("");
  const [filterSlide, setFilterSlide] = useState("");
  const [filterStudent, setFilterStudent] = useState("");
  const [sortCol, setSortCol] = useState<SortCol>("lesson");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");

  const lessonOptions = [
    ...new Set(
      allBites
        .map((b) =>
          b.lesson_name ?? (b.lesson_id !== null ? String(b.lesson_id) : null),
        )
        .filter((v): v is string => v !== null),
    ),
  ].sort();

  const slideOptions = [
    ...new Set(
      allBites
        .map((b) =>
          b.slide_index !== null ? String(b.slide_index) : null,
        )
        .filter((v): v is string => v !== null),
    ),
  ].sort((a, b) => Number(a) - Number(b));

  const studentOptions = [
    ...new Set(
      allBites
        .map((b) => b.name ?? b.username ?? b.user_id ?? null)
        .filter((v): v is string => v !== null),
    ),
  ].sort();

  const toggleSort = (col: SortCol) => {
    if (sortCol === col) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else {
      setSortCol(col);
      setSortDir("asc");
    }
  };

  const hasFilters = !!filterLesson || !!filterSlide || !!filterStudent;

  const displayed = allBites
    .filter((b) => {
      const lesson =
        b.lesson_name ?? (b.lesson_id !== null ? String(b.lesson_id) : "");
      const slide = b.slide_index !== null ? String(b.slide_index) : "";
      const student = b.name ?? b.username ?? b.user_id ?? "";
      if (filterLesson && lesson !== filterLesson) return false;
      if (filterSlide && slide !== filterSlide) return false;
      if (filterStudent && student !== filterStudent) return false;
      return true;
    })
    .sort((a, b) => {
      let cmp = 0;
      if (sortCol === "lesson") {
        const la = a.lesson_name ?? String(a.lesson_id ?? "");
        const lb = b.lesson_name ?? String(b.lesson_id ?? "");
        cmp = la.localeCompare(lb);
      } else if (sortCol === "slide") {
        cmp = (a.slide_index ?? 0) - (b.slide_index ?? 0);
      } else if (sortCol === "student") {
        const sa = a.name ?? a.username ?? a.user_id ?? "";
        const sb = b.name ?? b.username ?? b.user_id ?? "";
        cmp = sa.localeCompare(sb);
      } else if (sortCol === "score") {
        // push missing scores to the bottom regardless of sort direction
        if (a.quality_score === null && b.quality_score === null) cmp = 0;
        else if (a.quality_score === null) cmp = 1;
        else if (b.quality_score === null) cmp = -1;
        else cmp = a.quality_score - b.quality_score;
      } else if (sortCol === "time") {
        cmp = (a.attempt_spent_ms ?? 0) - (b.attempt_spent_ms ?? 0);
      } else if (sortCol === "qa_total") {
        cmp = (a.attempts_total ?? 0) - (b.attempts_total ?? 0);
      } else if (sortCol === "qa_incorrect") {
        cmp = (a.attempts_incorrect ?? 0) - (b.attempts_incorrect ?? 0);
      }
      return sortDir === "asc" ? cmp : -cmp;
    });

  return (
    <div id="all-bites" className="mt-6">
      <div className="flex items-center justify-between mb-1">
        <h2 className={SECTION_TITLE}>All Bites</h2>
        {hasFilters && (
          <Button
            variant="ghost"
            size="sm"
            className="h-6 text-xs text-muted-foreground gap-1"
            onClick={() => {
              setFilterLesson("");
              setFilterSlide("");
              setFilterStudent("");
            }}
          >
            <X className="size-3" /> Clear filters
          </Button>
        )}
      </div>
      <p className="text-xs text-muted-foreground mb-2">
        Q&amp;A Total = response rounds the student had; Incorrect Q&amp;A = rounds where they answered incorrectly. Shown for completed bites only.
      </p>

      <div className="overflow-x-auto rounded-lg border">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b bg-muted/40">
              {!isLesson && (
                <th className="px-3 py-2 w-96 text-left font-semibold uppercase tracking-wide text-muted-foreground text-[11px]">
                  <ComboFilter
                    label="Lesson"
                    options={lessonOptions}
                    value={filterLesson}
                    onChange={setFilterLesson}
                  />
                </th>
              )}
              <th className="px-3 py-2 w-14 text-left font-semibold uppercase tracking-wide text-muted-foreground text-[11px]">
                <div className="flex items-center gap-1">
                  <Select
                    value={filterSlide || "__all__"}
                    onValueChange={(v) =>
                      setFilterSlide(v === "__all__" ? "" : v)
                    }
                  >
                    <SelectTrigger className="h-6 w-16 text-xs border-0 bg-transparent p-0 shadow-none focus:ring-0 [&>svg]:size-3 text-muted-foreground font-semibold uppercase tracking-wide text-[11px]">
                      <SelectValue placeholder="Slide" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__all__">Slide</SelectItem>
                      {slideOptions.map((s) => (
                        <SelectItem key={s} value={s}>
                          Slide {s}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <button
                    className="text-muted-foreground hover:text-foreground transition-colors"
                    onClick={() => toggleSort("slide")}
                  >
                    <SortIcon col="slide" sortCol={sortCol} sortDir={sortDir} />
                  </button>
                </div>
              </th>
              <th className="px-3 py-2 text-left font-semibold uppercase tracking-wide text-muted-foreground text-[11px]">
                <ComboFilter
                  label="Student"
                  options={studentOptions}
                  value={filterStudent}
                  onChange={setFilterStudent}
                />
              </th>
              <th
                className="px-3 py-2 w-24 text-left font-semibold uppercase tracking-wide text-[11px] cursor-pointer select-none text-muted-foreground hover:text-foreground transition-colors"
                onClick={() => toggleSort("score")}
              >
                Score{" "}
                <SortIcon col="score" sortCol={sortCol} sortDir={sortDir} />
              </th>
              <th
                className="px-3 py-2 text-left font-semibold uppercase tracking-wide text-[11px] cursor-pointer select-none text-muted-foreground hover:text-foreground transition-colors"
                onClick={() => toggleSort("time")}
              >
                Time{" "}
                <SortIcon col="time" sortCol={sortCol} sortDir={sortDir} />
              </th>
              <th
                className="px-3 py-2 w-28 text-left font-semibold uppercase tracking-wide text-[11px] cursor-pointer select-none text-muted-foreground hover:text-foreground transition-colors"
                onClick={() => toggleSort("qa_incorrect")}
              >
                Incorrect Q&amp;A{" "}
                <SortIcon col="qa_incorrect" sortCol={sortCol} sortDir={sortDir} />
              </th>
              <th
                className="px-3 py-2 w-24 text-left font-semibold uppercase tracking-wide text-[11px] cursor-pointer select-none text-muted-foreground hover:text-foreground transition-colors"
                onClick={() => toggleSort("qa_total")}
              >
                Q&amp;A Total{" "}
                <SortIcon col="qa_total" sortCol={sortCol} sortDir={sortDir} />
              </th>
              <th className="px-3 py-2 text-right font-semibold uppercase tracking-wide text-muted-foreground text-[11px]">
                Attempt ID
              </th>
            </tr>
          </thead>
          <tbody>
            {displayed.length === 0 ? (
              <tr>
                <td
                  colSpan={isLesson ? 7 : 8}
                  className="px-3 py-6 text-center text-muted-foreground"
                >
                  No bites match the current filters.
                </td>
              </tr>
            ) : (
              displayed.map((b, i) => {
                const lessonLabel = b.lesson_name
                  ? `${b.lesson_name} (${b.lesson_id})`
                  : b.lesson_id !== null
                    ? String(b.lesson_id)
                    : "—";
                const student =
                  b.name ?? b.username ?? b.user_id ?? "—";
                const score =
                  b.quality_score !== null
                    ? String(b.quality_score)
                    : b.error
                      ? "err"
                      : "—";
                return (
                  <tr
                    key={i}
                    className="border-b last:border-0 hover:bg-muted/30 transition-colors"
                  >
                    {!isLesson && <td className="px-3 py-2">{lessonLabel}</td>}
                    <td className="px-3 py-2">
                      {b.slide_index !== null ? b.slide_index : "—"}
                    </td>
                    <td className="px-3 py-2">{student}</td>
                    <td className="px-3 py-2">{score}</td>
                    <td className="px-3 py-2">{fmtTime(b.attempt_spent_ms)}</td>
                    <td className="px-3 py-2">{b.attempt_status === "completed" ? (b.attempts_incorrect ?? "—") : "—"}</td>
                    <td className="px-3 py-2">{b.attempt_status === "completed" ? (b.attempts_total ?? "—") : "—"}</td>
                    <td className="px-3 py-2 text-right">
                      {b.sk && b.attempt_id !== null ? (
                        <button
                          className="inline-flex items-center gap-0.5 text-blue-500 hover:underline underline-offset-2 group"
                          title="View bite detail"
                          onClick={() =>
                            onOpenBite({
                              sessionId: b.session_id,
                              sk: b.sk!,
                              issueId: "",
                            })
                          }
                        >
                          {b.attempt_id}
                          <ArrowUpRight className="size-3 opacity-50 group-hover:opacity-100 transition-opacity" />
                        </button>
                      ) : (
                        b.attempt_id !== null ? String(b.attempt_id) : "—"
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <p className="mt-1 text-xs text-muted-foreground">
        Showing {displayed.length} of {allBites.length} bites
      </p>
    </div>
  );
}
